import React from 'react';
import UploadZone from './UploadZone';
import {interpolate} from './api';
import RichTextArea, {htmlToText} from './RichTextArea';

const isBlank = (value) => !value || !String(value).trim();

// Screen 1 — AI Intake (spec FR-005 – FR-011). Form state lives in DealIntakeApp so Back, Try again
// and Skip keep everything the buyer entered (SC-007).
export default function IntakeScreen({t, form, setForm, limits, acceptedExtensions, uploadUrl, errors, busy,
                                         onGenerate, onSkip, onUploadError}) {
    const nonBlankUrls = form.urls.filter((url) => !isBlank(url));
    const total = form.attachments.length + nonBlankUrls.length;
    const hasInput = !isBlank(htmlToText(form.description)) || form.attachments.length > 0 || nonBlankUrls.length > 0;
    const overLimit = total > limits.max_attachments;
    const canGenerate = hasInput && !overLimit && !busy;

    const setUrl = (index, value) => setForm((f) => ({...f, urls: f.urls.map((u, i) => (i === index ? value : u))}));
    const removeUrl = (index) => setForm((f) => ({...f, urls: f.urls.filter((_, i) => i !== index)}));
    const addUrl = () => setForm((f) => ({...f, urls: [...f.urls, '']}));
    const addAttachment = (file) => setForm((f) => ({...f, attachments: [...f.attachments, file]}));
    const removeAttachment = (id) => setForm((f) => ({...f, attachments: f.attachments.filter((a) => a.id !== id)}));

    return (
        <div className="di-page di-page--intake">
            <div className="di-header">
                <div className="di-header__icon" aria-hidden="true">✦</div>
                <h1 className="di-header__title">{t.heading}</h1>
                <p className="di-header__subtitle">{t.subheading}</p>
                <button type="button" className="di-cta di-cta--inline di-header__skip" onClick={onSkip} disabled={busy}>{t.skip}</button>
            </div>

            <div className="di-card">
                <section className="di-section">
                    <label className="di-label" htmlFor="di-description">{t.description_label}</label>
                    <div className="di-hint">{t.description_hint}</div>
                    <RichTextArea id="di-description" className="di-textarea" value={form.description}
                                  onChange={(html) => setForm((f) => ({...f, description: html}))}/>
                    <div className="di-checklist">
                        <div className="di-checklist__label">{t.description_checklist_label}</div>
                        <ul className="di-checklist__list">
                            {t.description_checklist.map((item) => (
                                <li key={item} className="di-checklist__item">
                                    <span className="di-checklist__tick" aria-hidden="true">✓</span>{item}
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>

                <section className="di-section">
                    <div className="di-label">{t.files_label} <span className="di-optional">{t.optional}</span></div>
                    <div className="di-hint">{t.files_hint}</div>
                    <UploadZone t={t} uploadUrl={uploadUrl} acceptedExtensions={acceptedExtensions}
                                maxFileBytes={limits.max_file_bytes} onUploaded={addAttachment}
                                onError={onUploadError}/>
                    {form.attachments.length > 0 && (
                        <ul className="di-files">
                            {form.attachments.map((file) => (
                                <li key={file.id} className="di-files__item">
                                    <span>{file.filename}</span>
                                    <button type="button" className="di-remove" aria-label={t.remove_file}
                                            onClick={() => removeAttachment(file.id)}>×</button>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <section className="di-section">
                    <div className="di-label">{t.urls_label} <span className="di-optional">{t.optional}</span></div>
                    <div className="di-hint">{t.urls_hint}</div>
                    {form.urls.map((url, index) => (
                        <div key={index} className="di-url-row">
                            <input type="url" className="di-input" value={url} placeholder={t.url_placeholder}
                                   onChange={(e) => setUrl(index, e.target.value)}/>
                            {form.urls.length > 1 && (
                                <button type="button" className="di-remove" aria-label={t.remove_url}
                                        onClick={() => removeUrl(index)}>×</button>
                            )}
                        </div>
                    ))}
                    <button type="button" className="di-link di-link--cyan" onClick={addUrl}>{t.add_url}</button>
                </section>
            </div>

            {overLimit && <div className="di-alert">{interpolate(t.over_limit, {max: limits.max_attachments})}</div>}
            {errors.length > 0 && <div className="di-alert">{errors.map((e) => <div key={e}>{e}</div>)}</div>}

            <button type="button" className="di-cta" disabled={!canGenerate} onClick={onGenerate}>{t.generate}</button>
            <div className="di-footer">
                <button type="button" className="di-link" onClick={onSkip} disabled={busy}>{t.skip}</button>
            </div>
        </div>
    );
}
